
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';

interface Company {
  id: string;
  name: string;
  description: string;
  active: boolean;
  created_at: string;
  users?: { count: number }[];
  clients?: { count: number }[];
  logo?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  company_id: string;
  company?: {
    id: string;
    name: string;
  };
}

interface SuperAdmin {
  id: string;
  name: string;
  email: string;
  active: boolean;
  created_at: string;
}

export default function SuperAdmin() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [superAdmins, setSuperAdmins] = useState<SuperAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<'overview' | 'company-detail' | 'super-admin-config'>('overview');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedCompanyUsers, setSelectedCompanyUsers] = useState<User[]>([]);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showSuperAdminForm, setShowSuperAdminForm] = useState(false);
  const [showEditSuperAdminForm, setShowEditSuperAdminForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [editingSuperAdmin, setEditingSuperAdmin] = useState<SuperAdmin | null>(null);
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    description: '',
    logo: ''
  });
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    company_id: ''
  });
  const [superAdminFormData, setSuperAdminFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  // Charger les données
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Charger les sociétés avec statistiques
      const companiesResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/companies-management?action=stats`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });
      const companiesResult = await companiesResponse.json();
      
      // Charger tous les utilisateurs
      const usersResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management?action=list`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });
      const usersResult = await usersResponse.json();

      if (companiesResult.success) {
        setCompanies(companiesResult.companies || []);
      }

      if (usersResult.success) {
        setUsers(usersResult.users || []);
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  // Charger les super admins depuis Supabase
  const loadSuperAdmins = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/super-admins-management?action=list`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });
      const result = await response.json();
      
      if (result.success) {
        setSuperAdmins(result.superAdmins || []);
      } else {
        console.error('Erreur chargement super admins:', result.error);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des super admins:', error);
    }
  };

  useEffect(() => {
    loadData();
    loadSuperAdmins();
    
    // Actualiser toutes les minutes
    const interval = setInterval(() => {
      loadData();
      loadSuperAdmins();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Voir les détails d'une société
  const viewCompanyDetails = (company: Company) => {
    setSelectedCompany(company);
    const companyUsers = users.filter(user => user.company_id === company.id);
    setSelectedCompanyUsers(companyUsers);
    setCurrentView('company-detail');
  };

  // Retour à la vue générale
  const backToOverview = () => {
    setCurrentView('overview');
    setSelectedCompany(null);
    setSelectedCompanyUsers([]);
  };

  // Voir la configuration des super admins
  const viewSuperAdminConfig = () => {
    setCurrentView('super-admin-config');
  };

  // Gérer les sociétés
  const handleCompanySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/companies-management`;
      const method = editingCompany ? 'PUT' : 'POST';
      const body = editingCompany 
        ? { id: editingCompany.id, ...companyFormData }
        : companyFormData;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();
      if (result.success) {
        await loadData();
        setShowCompanyForm(false);
        setEditingCompany(null);
        resetCompanyForm();
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Gérer les utilisateurs administrateurs
  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          name: userFormData.name,
          email: userFormData.email,
          password: userFormData.password,
          role: userFormData.role,
          company_id: userFormData.company_id
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadData();
        setShowUserForm(false);
        resetUserForm();
        // Si on est dans la vue détail, actualiser les utilisateurs de la société
        if (currentView === 'company-detail' && selectedCompany) {
          const updatedUsers = await loadUsers();
          const companyUsers = updatedUsers.filter(user => user.company_id === selectedCompany.id);
          setSelectedCompanyUsers(companyUsers);
        }
        alert('Utilisateur créé avec succès !');
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour recharger uniquement les utilisateurs
  const loadUsers = async () => {
    const usersResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management?action=list`, {
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
      }
    });
    const usersResult = await usersResponse.json();
    
    if (usersResult.success) {
      setUsers(usersResult.users || []);
      return usersResult.users || [];
    }
    return [];
  };

  const deleteCompany = async (companyId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette société ?')) {
      setLoading(true);
      try {
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/companies-management`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ id: companyId })
        });

        const result = await response.json();
        if (result.success) {
          await loadData();
          if (selectedCompany?.id === companyId) {
            backToOverview();
          }
        } else {
          alert('Erreur: ' + result.error);
        }
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur de connexion');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetCompanyForm = () => {
    setCompanyFormData({ name: '', description: '', logo: '' });
  };

  const resetUserForm = () => {
    setUserFormData({ name: '', email: '', password: '', role: 'admin', company_id: '' });
  };

  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    setCompanyFormData({
      name: company.name,
      description: company.description,
      logo: company.logo || ''
    });
    setShowCompanyForm(true);
  };

  // Gérer les super admins
  const handleSuperAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/super-admins-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          name: superAdminFormData.name,
          email: superAdminFormData.email,
          password: superAdminFormData.password
        })
      });

      const result = await response.json();
      
      if (result.success) {
        await loadSuperAdmins();
        setShowSuperAdminForm(false);
        resetSuperAdminForm();
        alert('Super Admin créé avec succès !');
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création du Super Admin');
    } finally {
      setLoading(false);
    }
  };

  // Modifier un super admin
  const handleEditSuperAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSuperAdmin) return;
    
    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/super-admins-management`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          id: editingSuperAdmin.id,
          name: superAdminFormData.name,
          email: superAdminFormData.email,
          password: superAdminFormData.password || undefined,
          active: editingSuperAdmin.active
        })
      });

      const result = await response.json();
      
      if (result.success) {
        await loadSuperAdmins();
        setShowEditSuperAdminForm(false);
        setEditingSuperAdmin(null);
        resetSuperAdminForm();
        alert('Super Admin modifié avec succès !');
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la modification du Super Admin');
    } finally {
      setLoading(false);
    }
  };

  // Changer le statut d'un super admin (actif/inactif)
  const toggleSuperAdminStatus = async (superAdmin: SuperAdmin) => {
    if (superAdmin.email === 'superadmin@crm.com' && superAdmin.active) {
      alert('Impossible de désactiver le Super Admin principal');
      return;
    }

    const newStatus = !superAdmin.active;
    const action = newStatus ? 'activer' : 'mettre en pause';
    
    if (confirm(`Êtes-vous sûr de vouloir ${action} ce Super Admin ?`)) {
      setLoading(true);
      
      try {
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/super-admins-management`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            id: superAdmin.id,
            active: newStatus
          })
        });

        const result = await response.json();
        
        if (result.success) {
          await loadSuperAdmins();
          alert(`Super Admin ${newStatus ? 'activé' : 'mis en pause'} avec succès !`);
        } else {
          alert('Erreur: ' + result.error);
        }
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur lors du changement de statut');
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteSuperAdmin = async (superAdminId: string) => {
    const superAdmin = superAdmins.find(admin => admin.id === superAdminId);
    
    if (superAdmin?.email === 'superadmin@crm.com') {
      alert('Impossible de supprimer le Super Admin principal');
      return;
    }

    if (confirm('Êtes-vous sûr de vouloir supprimer ce Super Admin ?')) {
      setLoading(true);
      
      try {
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/super-admins-management`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ id: superAdminId })
        });

        const result = await response.json();
        
        if (result.success) {
          await loadSuperAdmins();
          alert('Super Admin supprimé avec succès !');
        } else {
          alert('Erreur: ' + result.error);
        }
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur lors de la suppression');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditSuperAdmin = (superAdmin: SuperAdmin) => {
    setEditingSuperAdmin(superAdmin);
    setSuperAdminFormData({
      name: superAdmin.name,
      email: superAdmin.email,
      password: '' // Mot de passe vide pour modification optionnelle
    });
    setShowEditSuperAdminForm(true);
  };

  const resetSuperAdminForm = () => {
    setSuperAdminFormData({ name: '', email: '', password: '' });
  };

  if (loading && companies.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Chargement du panneau Super Admin...</p>
        </div>
      </div>
    );
  }

  // Vue configuration des super admins
  if (currentView === 'super-admin-config') {
    return (
      <div className="p-6">
        {/* Header avec navigation retour */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => setCurrentView('overview')}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="ri-arrow-left-line text-xl text-gray-600"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Configuration Super Admin</h1>
              <p className="text-gray-600">Gestion des super administrateurs du système</p>
            </div>
          </div>
          <Button onClick={() => setShowSuperAdminForm(true)}>
            <i className="ri-vip-crown-line mr-2"></i>
            Ajouter Super Admin
          </Button>
        </div>

        {/* Statistiques des super admins */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Total Super Admins</p>
                <p className="text-3xl font-bold">{superAdmins.length}</p>
              </div>
              <i className="ri-vip-crown-line text-3xl text-purple-200"></i>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Actifs</p>
                <p className="text-3xl font-bold">
                  {superAdmins.filter(admin => admin.active).length}
                </p>
              </div>
              <i className="ri-check-line text-3xl text-green-200"></i>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">En pause</p>
                <p className="text-3xl font-bold">
                  {superAdmins.filter(admin => !admin.active).length}
                </p>
              </div>
              <i className="ri-pause-line text-3xl text-orange-200"></i>
            </div>
          </div>
        </div>

        {/* Liste des super admins */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Super Administrateurs</h2>
            <p className="text-sm text-gray-600">Comptes avec accès complet au système</p>
          </div>
          
          {superAdmins.length === 0 ? (
            <div className="p-8 text-center">
              <i className="ri-vip-crown-line text-4xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">Aucun Super Admin trouvé</p>
              <Button onClick={() => setShowSuperAdminForm(true)} className="mt-4">
                <i className="ri-vip-crown-line mr-2"></i>
                Créer le premier Super Admin
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Super Admin</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Créé le</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {superAdmins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-vip-crown-line text-purple-600 text-lg"></i>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{admin.name}</p>
                            <p className="text-sm text-gray-500">Super Administrateur</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">{admin.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          admin.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {admin.active ? 'Actif' : 'En pause'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(admin.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEditSuperAdmin(admin)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Modifier"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => toggleSuperAdminStatus(admin)}
                            className={admin.active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                            title={admin.active ? 'Mettre en pause' : 'Activer'}
                            disabled={admin.email === 'superadmin@crm.com' && admin.active}
                          >
                            <i className={admin.active ? 'ri-pause-line' : 'ri-play-line'}></i>
                          </button>
                          <button
                            onClick={() => deleteSuperAdmin(admin.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                            disabled={admin.email === 'superadmin@crm.com'}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Informations importantes */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="ri-information-line text-yellow-600 mr-2"></i>
            <div>
              <p className="text-yellow-800 text-sm font-semibold">Important</p>
              <p className="text-yellow-700 text-xs">
                Les Super Admins ont un accès complet à toutes les sociétés et peuvent gérer l'ensemble du système.
                Le Super Admin principal ne peut pas être supprimé ou désactivé.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vue d'ensemble (liste des sociétés)
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Panneau Super Admin</h1>
          <p className="text-gray-600">Gestion des sociétés et administrateurs</p>
        </div>
        <div className="flex space-x-3">
          <Button onClick={() => setCurrentView('super-admin-config')} variant="secondary">
            <i className="ri-vip-crown-line mr-2"></i>
            Configuration
          </Button>
          <Button onClick={() => setShowUserForm(true)} variant="secondary">
            <i className="ri-user-add-line mr-2"></i>
            Créer Admin
          </Button>
          <Button onClick={() => setShowCompanyForm(true)}>
            <i className="ri-building-add-line mr-2"></i>
            Ajouter Société
          </Button>
        </div>
      </div>

      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Sociétés</p>
              <p className="text-3xl font-bold">{companies.length}</p>
            </div>
            <i className="ri-building-line text-3xl text-blue-200"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Total Utilisateurs</p>
              <p className="text-3xl font-bold">{users.length}</p>
            </div>
            <i className="ri-group-line text-3xl text-green-200"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Administrateurs</p>
              <p className="text-3xl font-bold">
                {users.filter(u => u.role === 'admin').length}
              </p>
            </div>
            <i className="ri-shield-user-line text-3xl text-purple-200"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Agents</p>
              <p className="text-3xl font-bold">
                {users.filter(u => u.role === 'agent' || u.role === 'call_center').length}
              </p>
            </div>
            <i className="ri-user-line text-3xl text-orange-200"></i>
          </div>
        </div>
      </div>

      {/* Liste des sociétés */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Sociétés</h2>
        </div>
        {companies.length === 0 ? (
          <div className="p-8 text-center">
            <i className="ri-building-line text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">Aucune société créée</p>
            <Button onClick={() => setShowCompanyForm(true)} className="mt-4">
              <i className="ri-building-add-line mr-2"></i>
              Créer la première société
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Société</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateurs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clients</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Créée le</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {companies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3 overflow-hidden bg-gray-100">
                          {company.logo ? (
                            <img 
                              src={company.logo} 
                              alt={`Logo ${company.name}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <i className="ri-building-line text-blue-600 text-lg"></i>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{company.name}</p>
                          <p className="text-sm text-gray-500">{company.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                        <span className="font-medium">{users.filter(u => u.company_id === company.id).length}</span>
                        <button
                          onClick={() => {
                            setSelectedCompany(company);
                            const companyUsers = users.filter(user => user.company_id === company.id);
                            setSelectedCompanyUsers(companyUsers);
                            setCurrentView('company-detail');
                          }}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {company.clients?.[0]?.count || 0}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(company.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedCompany(company);
                          const companyUsers = users.filter(user => user.company_id === company.id);
                          setSelectedCompanyUsers(companyUsers);
                          setCurrentView('company-detail');
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Voir les détails"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                      <button
                        onClick={() => {
                          setEditingCompany(company);
                          setCompanyFormData({
                            name: company.name,
                            description: company.description,
                            logo: company.logo || ''
                          });
                          setShowCompanyForm(true);
                        }}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Modifier"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={async () => {
                          if (confirm('Êtes-vous sûr de vouloir supprimer cette société ?')) {
                            setLoading(true);
                            try {
                              const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/companies-management`, {
                                method: 'DELETE',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
                                },
                                body: JSON.stringify({ id: company.id })
                              });

                              const result = await response.json();
                              if (result.success) {
                                await loadData();
                                if (selectedCompany?.id === company.id) {
                                  setCurrentView('overview');
                                  setSelectedCompany(null);
                                  setSelectedCompanyUsers([]);
                                }
                              } else {
                                alert('Erreur: ' + result.error);
                              }
                            } catch (error) {
                              console.error('Erreur:', error);
                              alert('Erreur de connexion');
                            } finally {
                              setLoading(false);
                            }
                          }
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Société avec Logo */}
      {showCompanyForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingCompany ? 'Modifier la société' : 'Nouvelle société'}
              </h2>
              <button
                onClick={() => {
                  setShowCompanyForm(false);
                  setEditingCompany(null);
                  resetCompanyForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleCompanySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la société *
                </label>
                <Input
                  value={companyFormData.name}
                  onChange={(e) => setCompanyFormData({
                    ...companyFormData,
                    name: e.target.value
                  })}
                  placeholder="Ex: TechCorp SARL"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={companyFormData.description}
                  onChange={(e) => setCompanyFormData({
                    ...companyFormData,
                    description: e.target.value
                  })}
                  placeholder="Description de la société..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo de la société (URL)
                </label>
                <Input
                  type="url"
                  value={companyFormData.logo}
                  onChange={(e) => setCompanyFormData({
                    ...companyFormData,
                    logo: e.target.value
                  })}
                  placeholder="https://exemple.com/logo.png"
                />
                {companyFormData.logo && (
                  <div className="mt-2 flex items-center">
                    <div className="w-8 h-8 rounded border overflow-hidden bg-gray-100 mr-2">
                      <img 
                        src={companyFormData.logo} 
                        alt="Prévisualisation logo"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full items-center justify-center text-xs text-gray-400 hidden">
                        Erreur
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">Prévisualisation du logo</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowCompanyForm(false);
                    setEditingCompany(null);
                    resetCompanyForm();
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit">
                  {editingCompany ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Utilisateur Admin */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedCompany && currentView === 'company-detail' 
                  ? `Ajouter un utilisateur à ${selectedCompany.name}`
                  : 'Créer un administrateur'
                }
              </h2>
              <button
                onClick={() => {
                  setShowUserForm(false);
                  resetUserForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <Input
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({
                    ...userFormData,
                    name: e.target.value
                  })}
                  placeholder="Ex: Jean Dupont"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({
                    ...userFormData,
                    email: e.target.value
                  })}
                  placeholder="utilisateur@societe.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <Input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({
                    ...userFormData,
                    password: e.target.value
                  })}
                  placeholder="Mot de passe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle *
                </label>
                <select
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({
                    ...userFormData,
                    role: e.target.value
                  })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                  required
                >
                  <option value="admin">Administrateur</option>
                  <option value="agent">Agent</option>
                </select>
              </div>

              {(!selectedCompany || currentView !== 'company-detail') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Société *
                  </label>
                  <select
                    value={userFormData.company_id}
                    onChange={(e) => setUserFormData({
                      ...userFormData,
                      company_id: e.target.value
                    })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                    required
                  >
                    <option value="">Sélectionnez une société</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowUserForm(false);
                    resetUserForm();
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Création...
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-line mr-2"></i>
                      {selectedCompany && currentView === 'company-detail' ? 'Ajouter' : 'Créer Admin'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Super Admin */}
      {showSuperAdminForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Créer un Super Admin</h2>
              <button
                onClick={() => {
                  setShowSuperAdminForm(false);
                  resetSuperAdminForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSuperAdminSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <Input
                  value={superAdminFormData.name}
                  onChange={(e) => setSuperAdminFormData({
                    ...superAdminFormData,
                    name: e.target.value
                  })}
                  placeholder="Ex: Jean Super Admin"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={superAdminFormData.email}
                  onChange={(e) => setSuperAdminFormData({
                    ...superAdminFormData,
                    email: e.target.value
                  })}
                  placeholder="superadmin@domain.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <Input
                  type="password"
                  value={superAdminFormData.password}
                  onChange={(e) => setSuperAdminFormData({
                    ...superAdminFormData,
                    password: e.target.value
                  })}
                  placeholder="Mot de passe sécurisé"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowSuperAdminForm(false);
                    resetSuperAdminForm();
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Création...
                    </>
                  ) : (
                    <>
                      <i className="ri-vip-crown-line mr-2"></i>
                      Créer Super Admin
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Modification Super Admin */}
      {showEditSuperAdminForm && editingSuperAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Modifier Super Admin</h2>
              <button
                onClick={() => {
                  setShowEditSuperAdminForm(false);
                  setEditingSuperAdmin(null);
                  resetSuperAdminForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleEditSuperAdminSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <Input
                  value={superAdminFormData.name}
                  onChange={(e) => setSuperAdminFormData({
                    ...superAdminFormData,
                    name: e.target.value
                  })}
                  placeholder="Ex: Jean Super Admin"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={superAdminFormData.email}
                  onChange={(e) => setSuperAdminFormData({
                    ...superAdminFormData,
                    email: e.target.value
                  })}
                  placeholder="superadmin@domain.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nouveau mot de passe (optionnel)
                </label>
                <Input
                  type="password"
                  value={superAdminFormData.password}
                  onChange={(e) => setSuperAdminFormData({
                    ...superAdminFormData,
                    password: e.target.value
                  })}
                  placeholder="Laisser vide pour garder l'ancien"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Laisser vide pour conserver le mot de passe actuel
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowEditSuperAdminForm(false);
                    setEditingSuperAdmin(null);
                    resetSuperAdminForm();
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Modification...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line mr-2"></i>
                      Modifier
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
